// Ported from dev-scrips-pm-react src/patients/components/chart/documents-chart-tab.tsx (PROD-1119 Patients UI port).
/**
 * DocumentsChartTab — Documents inner tab of the patient Chart shell.
 *
 * Flutter ref: `patient_chart/.../tab_widgets/doc_tab.dart` + the
 * `document_module/document_list_widget.dart` card.
 *
 * Renders the **shared card list** (`DocumentList` / `DocumentRow`) — the
 * same card design + row actions used by the appointment-panel Docs tab —
 * under the chart's Status (Active / Archived) toggle + Filters toolbar.
 * (Earlier this tab shipped a dense 9-column table, which drifted from the
 * Flutter card design; converted to cards 2026-06-09 to match the design
 * + unify with the appointment-panel surface.)
 *
 * Status / Filters / pagination are chart-specific — Flutter's chart
 * `doc_tab.dart` carries the "View Active/Archive Docs" toggle + "Filters"
 * (the appointment-panel variant is the minimal header+add+list). Per-row
 * actions (View / Mark Reviewed / Edit / Archive ↔ Unarchive) live on each
 * `DocumentRow`, which owns its own mutations + confirm dialog.
 */

import { ChartEmpty } from './chart-empty';
import { useReportChartCount } from './chart-page-context';
import { ChartShowAll } from './chart-row-cap';
import { useRowCap } from './use-row-cap';
import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNoteBlockActionsHost } from '@/encounter/structured-note/note-add-action';
import { isEncounterFlagOn } from '@/shared/lib/encounter-flags';
import { useQueryClient } from '@tanstack/react-query';
import { Filter } from 'lucide-react';
import { EncounterSectionChrome } from '@scripsteam/scrips-signal-ds';
import { Plus, Upload } from 'lucide-react';
import { SectionIconAction } from '@/encounter/section-icon-action';
import { Button, Segmented } from '@scripsteam/scrips-signal-ds';
import { usePatientDocuments } from '@/patients/services/use-patient-documents';
import { DocumentList } from '@/documents/components/document-list';
import { NewFilePopup } from '@/documents/components/new-file-popup';
import {
  DocumentsFilterPanel,
  EMPTY_DOCUMENTS_FILTERS,
  type DocumentsFilterValue,
} from '@/documents/components/documents-filter-panel';
import { useDocumentUploadedBy } from '@/documents/services/use-document-uploaded-by';
import { patientDocumentToModel } from '@/patients/lib/patient-document-adapter';
import { useAuthStore } from '@/auth/auth-store';
import { DOCTOR_ROLE } from '@/auth/roles';
import { ConsentFillSignPopup, ConsentExternalUploadDialog } from '@/consent/components';
import { useProviderConsentMarks } from '@/consent/services';
import type { ConsentTemplate } from '@/consent/types/consent-template-api';
import { ChartSectionHeader } from './chart-section-header';
import { EmptyState } from '@scripsteam/scrips-signal-ds';

const PAGE_SIZE = 20;

export interface DocumentsChartTabProps {
  patientId: string | null | undefined;
  /**
   * When set, every "Add document" calls this instead of opening `NewFilePopup` (PROD-3204: the chart's Documents tab
   * hands the upload to Document Intake behind `?x=chart-intake`). Unset = the popup, as before.
   */
  onAddDocument?: () => void;
  /**
   * The encounter new uploads are STAMPED onto. This does NOT filter the list —
   * see `listScope`. Flutter keeps these two jobs separate and BOTH surfaces stamp:
   * the chart's own Docs tab passes it to `NewFilePopupWidget` + the save event
   * (`chart/tab_widgets/doc_tab.dart:222,554,679`) while listing patient-wide, and
   * the encounter Docs step does the same (`encounter_docs_screen.dart:127,241,342`).
   */
  encounterId?: string | null;
  /**
   * Which documents the LIST shows. Flutter has two distinct doc surfaces and they
   * scope differently — conflating them was the port bug this prop fixes:
   *
   *   'patient' (default) — every document on file for the patient. This is the
   *     CHART's Docs tab, on both its mounts (patient-edit AND inside an encounter).
   *     Flutter `chart/tab_widgets/doc_tab.dart:619-638` `fetchDocuments()` builds
   *     `PatientDocumentRequestParam(patientId, pageNumber, dates, fileType,
   *     uploadedBy, isArchived)` — note it passes NO encounterId, even though the
   *     widget receives one for the stamp above.
   *
   *   'encounter' — only documents filed against `encounterId`. This is the
   *     encounter's own Docs STEP, whose list is `pEncounterBloc.state.patientDocs`
   *     (`encounter_docs_screen.dart:137`), filled by `FetchPatientEncounterDocument`
   *     → `PatientDocumentRequestParam(patientId: subjectId(), encounterId:
   *     encounterId(), pageNumber)` (`patient_encounter_bloc.dart:2452-2461`).
   *
   * Defaulting to 'patient' keeps the chart correct wherever it is mounted; only the
   * encounter step opts in.
   */
  listScope?: 'patient' | 'encounter';
  /**
   * WHICH SURFACE IS MOUNTING THIS.
   *
   * `chart` (default) — the patient Chart ▸ Docs tab: eyebrow header with a count,
   * the Active/Archived Status segment, the Filters sheet trigger and Previous/Next.
   *
   * `encounter-step` — the encounter Docs STEP. The deployed app draws a DIFFERENT screen
   * here: `encounter_docs_screen.dart:62-75` is a section header (identity tile + "Docs" +
   * two actions) over a bare list. It has **no** Status segment, **no** Filters button and
   * **no** pager. Mounting the Chart component wholesale is what put all three on the step.
   *
   * PAGINATION — the deployed step has NONE, and not by oversight. `DocumentListWidget` does
   * emit `loadMore` on scroll (`document_list_widget.dart:29-36, 60-62`), but the encounter
   * screen's callback is an if/else over `markAsReviewed` / `edit` / `delete` only
   * (`encounter_docs_screen.dart:138-150`) — `loadMore` falls off the end. With
   * `defaultPatientDocumentPageSize = 20` (`patient_document_request_param.dart:7, 18`), the
   * deployed step shows the first 20 documents and never advances. Our PAGE_SIZE is also 20,
   * so dropping the pager here is exact parity, NOT a silent capability cut.
   * ⚠️ OWED: it does mean documents 21+ on one encounter are unreachable from this surface —
   * true of the deployed app too, but worth a ruling if an encounter ever carries that many.
   *
   * ⚠️ ARCHIVE DEAD-END (parity with deployed, still a real trap): the row menu can still
   * archive a document, and with the Status segment gone there is no way to see or unarchive
   * it from this surface. Flutter has the same shape. Recovery is Chart ▸ Docs.
   */
  surface?: 'chart' | 'encounter-step';
  /**
   * "Add Order" — the encounter step's SECOND header action (encounter-step surface only).
   *
   * `encounter_docs_screen.dart:154-161` overrides `menuOptions` to exactly
   * `[moreOptionsButton(), addDocButton()]`, and `moreOptionsButton`
   * (`common_encounter_component_base_tab.dart:385-398`) is the sparkle glyph
   * (`ScripsIcons.custom`) with the literal tooltip "Add Order" — the same action A/P already
   * wires to its own sparkle. Optional and rendered only when supplied: an action with no host
   * is omitted, never drawn dead (anti-fiction).
   */
  onAddOrder?: () => void;
  /**
   * Frame v2 only (encounter-step surface): the list is drawn inside the note's "Documents" block, which
   * already titles it, so the step's section header (identity tile + "Docs") is not drawn — one title per
   * block (the independent review, 6 Oct). Its two actions stay, on a row of their own. Absent → the
   * header exactly as before (OFF, and the Chart tab).
   */
  inNote?: boolean;
}

export function DocumentsChartTab({
  patientId,
  encounterId,
  listScope = 'patient',
  surface = 'chart',
  onAddOrder,
  inNote = false,
  onAddDocument,
}: DocumentsChartTabProps) {
  const encounterStep = surface === 'encounter-step';
  const [page, setPage] = useState(1);
  const [uploadOpen, setUploadOpen] = useState(false);
  // PROD-3204: with `onAddDocument` the upload belongs to Document Intake; otherwise the popup, as before.
  const openAdd = () => (onAddDocument ? onAddDocument() : setUploadOpen(true));
  // The frame v2 note block's header node (null outside a block): the in-note actions draw there.
  const actionsHost = useNoteBlockActionsHost();
  const [docsBlockV2] = useState(() => isEncounterFlagOn('docsBlockV2'));
  // PROD-1051 doc-forms — when a document-mode consent template is picked in the New File
  // popup's Form flow, swap to the in-app fill-and-sign popup.
  const [signTemplate, setSignTemplate] = useState<ConsentTemplate | null>(null);
  // PROD-1243 (Case 3) — the consent was signed outside the app; upload the finished PDF.
  const [uploadTemplate, setUploadTemplate] = useState<ConsentTemplate | null>(null);
  const queryClient = useQueryClient();
  const userId = useAuthStore((s) => s.user?.userId ?? null);
  const actorName = useAuthStore((s) =>
    [s.user?.firstName, s.user?.lastName].filter(Boolean).join(' ').trim() || null,
  );
  // PROD-900: Active (default) vs Archived list toggle → server-side
  // `isArchived` filter; each DocumentRow flips its own Archive ↔ Unarchive
  // label off the row's `isDeleted`.
  const [showArchived, setShowArchived] = useState(false);
  // PROD-900 follow-up: full filter sheet (Time Period / File Type /
  // Uploaded By). Status stays inline; sheet has `hideStatusSection`.
  const [filterOpen, setFilterOpen] = useState(false);
  const [filters, setFilters] = useState<DocumentsFilterValue>(
    EMPTY_DOCUMENTS_FILTERS,
  );
  // Only feeds DocumentsFilterPanel, which the encounter step does not render — skip the
  // request rather than firing it once per step mount for a panel with no trigger.
  const uploadedByQuery = useDocumentUploadedBy(surface === 'chart' ? (patientId ?? undefined) : undefined);

  // Reset pagination + filters when the patient changes.
  useEffect(() => {
    setPage(1);
    setFilters(EMPTY_DOCUMENTS_FILTERS);
  }, [patientId]);
  // Reset to page 1 when flipping the Status segment.
  useEffect(() => {
    setPage(1);
  }, [showArchived]);

  const { data, isFetching, isError, refetch } = usePatientDocuments({
    patientId,
    // Encounter id filters the list ONLY for the encounter Docs step. The chart's Docs
    // tab lists patient-wide even when mounted inside an encounter — Flutter's chart
    // `fetchDocuments()` sends no encounterId (see `listScope`).
    encounterId: listScope === 'encounter' ? encounterId : null,
    pageNumber: page,
    pageSize: PAGE_SIZE,
    isArchived: showArchived,
    startDate: filters.startDate ?? null,
    endDate: filters.endDate ?? null,
    fileType: filters.fileType,
    uploadedBy: filters.uploadedBy,
  });

  const filterCount =
    (filters.startDate ? 1 : 0) +
    (filters.endDate ? 1 : 0) +
    filters.fileType.length +
    filters.uploadedBy.length;

  const applyFilters = (next: DocumentsFilterValue) => {
    setFilters(next);
    setPage(1);
  };

  const rows = data ?? [];
  // The shared card list (DocumentList/DocumentRow) consumes the
  // documents-module `PatientDocumentModel`; map from the chart read-side
  // `PatientDocument` via the explicit adapter (no unknown-cast).
  const models = useMemo(() => rows.map(patientDocumentToModel), [rows]);
  // Wire ships a flat array (no totalCount), so mirror Labs/Allergies:
  // offer "Next" while the page is full.
  const hasMore = (data?.length ?? 0) === PAGE_SIZE;
  // The chart's list shows its first 5 with "Show all N" (N: the rows of this page); the encounter's Docs step is
  // not capped. The index's count is the same (chart surface only).
  const cap = useRowCap(models);
  useReportChartCount(encounterStep || (isFetching && rows.length === 0) ? null : rows.length);

  if (!patientId) {
    return (
      <div className="p-6">
        <EmptyState
            icon="info"
            title="Save the patient first to view documents."
          />
      </div>
    );
  }

  // In the note block header: with `docsBlockV2` ON, full secondary buttons, icon and label (PROD-3397, Antonin 3 Nov: "upload
  // document should be a full button with icon"; catalog DocsBlock, feat/signal 5c52a404). Same handlers. `?x=-docs-block-v2`:
  // the icon buttons, as before.
  const inNoteActions = !docsBlockV2 ? (
    <>
      {onAddOrder ? <SectionIconAction icon={Plus} label="Add Order" onClick={onAddOrder} /> : null}
      <SectionIconAction icon={Upload} label="Add document" onClick={() => openAdd()} />
    </>
  ) : (
    <>
      {onAddOrder ? (
        <Button type="button" size="sm" variant="secondary" onClick={onAddOrder} icon={<Plus className="size-4" aria-hidden />} iconPosition="leading">
          Add Order
        </Button>
      ) : null}
      <Button type="button" size="sm" variant="secondary" onClick={() => openAdd()} icon={<Upload className="size-4" aria-hidden />} iconPosition="leading">
        Add document
      </Button>
    </>
  );

  // `document_list_widget.dart:53` — the deployed step's empty state is the bare string
  // "No documents yet"; the Chart's longer guidance copy is Chart-only.
  // Inside the frame v2 note the visit's list says so (export 21-10 EncounterDocumentsStep).
  const emptyMessage = encounterStep
    ? inNote
      ? 'No documents for this encounter yet.'
      : 'No documents yet'
    : filterCount > 0
      ? 'No documents match your filters. Adjust or clear filters to see more.'
      : showArchived
        ? 'No archived documents.'
        : 'No documents recorded for this patient.';

  return (
    /* `pa-docs-step` was the HOST-OWNED hook for §26 of the PA's encounter-design-overrides.css
       (the deployed step's card row anatomy). That stylesheet is deleted (PA-Z, 2026-09-23), so
       no rule reads the class now; it stays as the surface marker
       documents-chart-tab.encounter-step.test.tsx pins. */
    <div className={encounterStep ? 'pa-docs-step' : 'p-6'} data-slot={encounterStep ? undefined : 'documents-tab'}>
      {encounterStep && inNote ? (
        // Frame v2: the block titles the list; its live actions are drawn IN the block header (export 21-10
        // EncounterDocumentsStep / nurse-5-documents.html: Add order and Add document beside the "Documents" title),
        // portalled into the block's header node; same handlers. Outside a block (no node) they keep their own row.
        showArchived ? null : inNoteActions && actionsHost ? (
          createPortal(inNoteActions, actionsHost)
        ) : (
          <div role="toolbar" aria-label="Documents actions" data-testid="docs-in-note-actions" className="mb-3 flex flex-row items-center justify-end gap-2">
            {inNoteActions}
          </div>
        )
      ) : encounterStep ? (
        <div className="mb-8 px-1">
        <EncounterSectionChrome
          frame="plain"
          icon="docs"
          title="Docs"
          titleTone="depth"
          data-testid="docs-section-chrome"
          actions={
            showArchived ? undefined : (
              
              <>
                {/* Add Order FIRST, then Add document — `encounter_docs_screen.dart:154-161` lists
                    `[moreOptionsButton(), addDocButton()]` in that order. Icon pass (16 Oct): Lucide, Add Order the
                    plus (sparkle is Scrips Intelligence's), Add document the upload it opens. */}
                {onAddOrder ? (
                  <SectionIconAction
                    icon={Plus}
                    label="Add Order"
                    onClick={onAddOrder}
                  />
                ) : null}
                <SectionIconAction
                  icon={Upload}
                  label="Add document"
                  onClick={() => openAdd()}
                />
              </>
            )
          }
        />
        </div>
      ) : (
        /* Header — Documents + count + "+" add (Flutter doc_tab.dart:257).
           Add is hidden on the Archived list (you upload to Active). */
        // The design's solo Documents section (chart-documents.html `solo('Documents', null, …)`): the title and count;
        // Add document moved to the toolbar's right end (PaDocumentList), same handler.
        <ChartSectionHeader title="Documents" count={rows.length} />
      )}

      {/* Toolbar — inline Status segment (primary axis) + Filters sheet
          trigger (Flutter "View Active/Archive Docs" + "Filters"). Chart only:
          neither control exists on the deployed encounter step. */}
      {!encounterStep && (
      // The design's toolbar (PaDocumentList `.toolbar`): the Active / Archived pill segment, Filters (with the PA's
      // active-filter count), then "Add document" at the right (hidden on the Archived list, as before).
      <div data-slot="documents-toolbar">
        <Segmented<'active' | 'archived'>
          aria-label="Document status"
          variant="pill"
          value={showArchived ? 'archived' : 'active'}
          onChange={(v) => setShowArchived(v === 'archived')}
          options={[
            { value: 'active', label: 'Active' },
            { value: 'archived', label: 'Archived' },
          ]}
        />
        <Button
          type="button"
          size="sm"
          variant="secondary"
          onClick={() => setFilterOpen(true)}
          className="gap-1"
          icon={<Filter className="size-4" aria-hidden />}
          iconPosition="leading"
          aria-label={
            filterCount > 0 ? `Filters (${filterCount} active)` : 'Filters'
          }
        >
          Filters
          {filterCount > 0 ? (
            <span data-slot="documents-filter-count">{filterCount}</span>
          ) : null}
        </Button>
        <span data-slot="documents-toolbar-spacer" />
        {showArchived ? null : (
          <Button
            type="button"
            size="sm"
            variant="secondary"
            onClick={() => openAdd()}
            icon={<Upload className="size-4" aria-hidden />}
            iconPosition="leading"
          >
            Add document
          </Button>
        )}
      </div>
      )}

      {/* Shared upload popup (same NewFilePopup as the appointment-panel
          Docs tab) — encounter-scoped when composed as the encounter Docs
          step, else patient-scoped (`encounterId: null`). */}
      {onAddDocument ? null : (
      <NewFilePopup
        open={uploadOpen}
        onOpenChange={setUploadOpen}
        patientId={patientId}
        encounterId={encounterId ?? null}
        onPickConsentTemplate={(t) => {
          setUploadOpen(false);
          setSignTemplate(t);
        }}
        onUploadSignedConsent={(t) => {
          setUploadOpen(false);
          setUploadTemplate(t);
        }}
      />
      )}
      {uploadTemplate && (
        <ConsentExternalUploadDialog
          open
          onOpenChange={(o) => !o && setUploadTemplate(null)}
          patientId={patientId}
          template={uploadTemplate}
          // Stamp the ingested consent onto the encounter it was filed from — same rule as
          // NewFilePopup above: always stamp, never scope. Without it the Type="Form" doc
          // lands with encounterId: null and the encounter Docs step can never show it.
          encounterId={encounterId ?? null}
          actorId={userId}
          actorName={actorName}
          onIngested={() => {
            // The ingested consent lands as a new Type="Form" PatientDocument — refetch the
            // Docs list so it appears without a manual refresh (the hook also invalidates).
            void queryClient.invalidateQueries({ queryKey: ['patient-documents', patientId] });
            setUploadTemplate(null);
          }}
        />
      )}
      {signTemplate && (
        <ConsentFillSign
          patientId={patientId}
          template={signTemplate}
          encounterId={encounterId ?? null}
          actorId={userId}
          onClose={() => setSignTemplate(null)}
          onSigned={() => {
            // The signed consent lands as a new Type="Form" PatientDocument — refetch the
            // Docs list so it appears without a manual refresh (GetPatientDocuments re-runs).
            void queryClient.invalidateQueries({ queryKey: ['patient-documents', patientId] });
            setSignTemplate(null);
          }}
        />
      )}
      {!encounterStep && (
      <DocumentsFilterPanel
        open={filterOpen}
        onOpenChange={setFilterOpen}
        value={filters}
        onApply={applyFilters}
        uploaders={uploadedByQuery.data}
        uploadersLoading={uploadedByQuery.isLoading}
        hideStatusSection
      />
      )}

      {/* Card list — same DocumentRow design + actions as the
          appointment-panel surface. */}
      {!encounterStep && rows.length === 0 && page === 1 && filterCount === 0 && !showArchived && !isFetching && !isError ? (
        <ChartEmpty title="No documents recorded for this patient." ctaLabel="Add document" onCta={() => openAdd()} />
      ) : (
      <DocumentList
        documents={encounterStep ? models : cap.visible}
        isLoading={isFetching && rows.length === 0}
        isError={isError}
        onRetry={() => void refetch()}
        emptyMessage={emptyMessage}
        emptyVariant={encounterStep ? 'bare' : 'card'}
        surface={surface}
      />
      )}

      {!encounterStep ? <ChartShowAll cap={cap} label="documents" /> : null}

      {/* The pager waits behind "Show all N" on the first page (the page is read in full before the next one). */}
      {!encounterStep && (rows.length > 0 || page > 1) && (page > 1 || !cap.capped || cap.expanded) ? (
        <div data-slot="documents-pager">
          <span data-slot="documents-pager-page">
            Showing page {page}
            {hasMore ? '' : ' (end)'}
          </span>
          <div data-slot="documents-pager-buttons">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1 || isFetching}
            >
              Previous
            </Button>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => setPage((p) => p + 1)}
              disabled={!hasMore || isFetching}
            >
              Next
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Consent fill & sign (PROD-1051) — only mounts while a document-mode template
// is being signed, so the signed-in provider signature + clinic-stamp GETs
// (useProviderConsentMarks) fire on demand rather than on every Docs-tab open.
// ──────────────────────────────────────────────────────────────────────────

function ConsentFillSign({
  patientId,
  template,
  encounterId,
  actorId,
  onClose,
  onSigned,
}: {
  patientId: string;
  template: ConsentTemplate;
  /** Encounter the consent is being signed in, so the resulting Form document is filed
   *  against it. Null on the patient-chart mount, where there is no encounter. */
  encounterId: string | null;
  actorId: string | null;
  onClose: () => void;
  onSigned: () => void;
}) {
  const { providerSignatureUrl, clinicStampUrl, providerName, providerSignatureLoading } =
    useProviderConsentMarks();
  // A signed-in doctor auto-signs with their own signature (useProviderConsentMarks
  // resolves it); anyone else (nurse/admin/allied health) must pick the treating
  // provider in the popup when the template needs a provider signature.
  const isDoctor = useAuthStore((s) => s.user?.role) === DOCTOR_ROLE;
  return (
    <ConsentFillSignPopup
      open
      onOpenChange={(o) => !o && onClose()}
      patientId={patientId}
      template={template}
      encounterId={encounterId}
      actorId={actorId}
      providerSignatureUrl={providerSignatureUrl}
      clinicStampUrl={clinicStampUrl}
      providerName={providerName}
      providerSignatureLoading={providerSignatureLoading}
      allowProviderPick={!isDoctor}
      onSigned={onSigned}
    />
  );
}
